import Box from "@mui/material/Box";
import { useDropzone } from "react-dropzone";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material";
import Typography from "@mui/material/Typography";
import { getIconComponentByType } from "../../utils/assets";
import React, { useEffect, useState, useRef } from "react";

const PaperStyled = styled(Paper)(({ theme }) => ({
  width: "100%",
  height: 184,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
}));

const FileDropBox = ({
  name,
  label,
  acceptFileType,
  onFileDrop,
  multiple = false,
  error = "",
  value = null,
  message,
  is_file_accepted = true,
  resetFileCount,
  reset,
  maxSize,
  isGCPFile = false, // New prop added here
  ...props
}) => {
  const [errors, setErrors] = useState("");
  const [acceptMessage, setAcceptMessage] = useState("");
  const fileCount = useRef(0);

  const handleFileReject = (fileRejections) => {
    setErrors(fileRejections[0].errors[0].message);
    setAcceptMessage("");
  };

  const handleFileAccept = (acceptedFiles) => {
    // Additional validation based on acceptFileType
    let filteredFiles = acceptedFiles;
    
    // Check if acceptFileType is an object with image MIME types (for image files)
    const isImageAccept = acceptFileType && typeof acceptFileType === 'object' && 
      Object.keys(acceptFileType).some(key => key.startsWith('image/'));
    
    if (acceptFileType === "image/*" || isImageAccept) {
      // For image files, filter to only include actual image files
      filteredFiles = acceptedFiles.filter(file => 
        file.type && file.type.startsWith('image/')
      );
      if (filteredFiles.length !== acceptedFiles.length) {
        setErrors("Only image files are allowed");
        return;
      }
    } else if (isGCPFile) {
      // For GCP files, filter to only include CSV files
      filteredFiles = acceptedFiles.filter(file => {
        const fileName = file.name.toLowerCase();
        const fileType = file.type?.toLowerCase() || '';
        return fileName.endsWith('.csv') || 
               fileType === 'text/csv' || 
               fileType === 'application/csv' ||
               fileType === 'text/x-csv' ||
               fileType === 'application/x-csv';
      });
      if (filteredFiles.length !== acceptedFiles.length) {
        setErrors("Only CSV files are allowed");
        return;
      }
    }
    
    const file = filteredFiles[0];
    if (maxSize && file.size > maxSize) {
      setErrors(`File size exceeds ${maxSize / 1024 / 1024}MB`);
    } else {
      setErrors("");
      onFileDrop(filteredFiles);

      // Reset the file count if it's a GCP file
      if (isGCPFile) {
        fileCount.current = 1;
      } else {
        fileCount.current += filteredFiles.length;
      }

      if (is_file_accepted) {
        // For GCP files, show file name; for others, show count
        const displayMessage = isGCPFile && file.name
          ? file.name
          : `${fileCount.current} file${fileCount.current > 1 ? 's' : ''} selected`;
        setAcceptMessage(displayMessage);
      }
    }
  };

  useEffect(() => {
    setErrors(error);
    if (!value) {
      setAcceptMessage("");
    } else {
      // For GCP files only, use value prop to maintain file status across re-renders
      // For other files (like image uploads), preserve existing behavior
      if (isGCPFile) {
        setAcceptMessage(value);
      }
    }
    if (resetFileCount) {
      fileCount.current = 0;
      setAcceptMessage("");
    }
  }, [error, value, resetFileCount, isGCPFile]);

  useEffect(() => {
    if (reset) {
      setAcceptMessage("");
      fileCount.current = 0;
    }
  }, [reset]);

  // Normalize acceptFileType to ensure proper file dialog filtering
  const normalizedAccept = React.useMemo(() => {
    if (!acceptFileType) return undefined;
    
    // If it's already an object, use it as-is
    if (typeof acceptFileType === 'object') {
      return acceptFileType;
    }
    
    // If it's a string like "image/*", convert to object format for better browser support
    if (acceptFileType === "image/*") {
      return {
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'image/gif': ['.gif'],
        'image/bmp': ['.bmp'],
        'image/webp': ['.webp'],
        'image/tiff': ['.tiff', '.tif']
      };
    }
    
    // For CSV files (string format like ".csv"), convert to object format for better filtering
    if (isGCPFile && typeof acceptFileType === 'string' && acceptFileType.includes('.csv')) {
      return {
        'text/csv': ['.csv'],
        'application/csv': ['.csv'],
        'text/x-csv': ['.csv'],
        'application/x-csv': ['.csv']
      };
    }
    
    // For other string formats, return as-is (react-dropzone handles them)
    return acceptFileType;
  }, [acceptFileType, isGCPFile]);

  // Create validator function based on acceptFileType
  const validator = (file) => {
    // Check if acceptFileType is an object with image MIME types
    const isImageAccept = normalizedAccept && typeof normalizedAccept === 'object' && 
      Object.keys(normalizedAccept).some(key => key.startsWith('image/'));
    
    if (acceptFileType === "image/*" || isImageAccept) {
      // Validate image files
      if (!file.type || !file.type.startsWith('image/')) {
        return {
          code: 'file-invalid-type',
          message: 'Only image files are allowed'
        };
      }
    } else if (isGCPFile) {
      // Validate CSV files
      const fileName = file.name.toLowerCase();
      const fileType = file.type?.toLowerCase() || '';
      const isCSV = fileName.endsWith('.csv') || 
                    fileType === 'text/csv' || 
                    fileType === 'application/csv' ||
                    fileType === 'text/x-csv' ||
                    fileType === 'application/x-csv';
      if (!isCSV) {
        return {
          code: 'file-invalid-type',
          message: 'Only CSV files are allowed'
        };
      }
    }
    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: normalizedAccept,
    validator: validator,
    onDropAccepted: handleFileAccept,
    onDropRejected: handleFileReject,
    multiple: multiple,
    ...props,
  });

  const { ref, ...rootProps } = getRootProps();

  return (
    <Box ref={ref}>
      <PaperStyled {...rootProps} variant="outlined">
        <input {...getInputProps()} name={name} />
        {getIconComponentByType("cloudUpload")}
        <Typography variant="h6">Drag & Drop</Typography>
        <Typography variant="h6">{label}</Typography>
        <Typography>{message}</Typography>
        {errors && (
          <Typography
            style={{
              color: "red",
              padding: 5,
              margin: 0,
              fontSize: 14,
            }}
          >
            {errors}
          </Typography>
        )}
        {acceptMessage && (
          <Typography
            noWrap
            style={{ color: "green", padding: 5, margin: 0, fontSize: 14 }}
          >
            {acceptMessage.length > 20
              ? `${acceptMessage.slice(0, 20)}...`
              : acceptMessage}
          </Typography>
        )}
      </PaperStyled>
    </Box>
  );
};

export default FileDropBox;
